import { addWeeks, differenceInCalendarDays, differenceInWeeks, format, getDay, parse } from "date-fns"
import { WeeklyTKData, WeeklyTKApproversByStatus, WeeklyTKEmployeeDataByDay, WeeklyTKEmployeeWCsByDay, WeeklyTKReferencedIds } from "./types"
import { iEmployee } from "../../cached-data/types"
import { DATE_MM_DD_YYYY, STORED_DATE_ONLY_FORMAT, TIME_FORMAT } from "../../common/constants"
import { Absence, EmployeeWorkShiftStartStopTime, TimekeepingEntry, tResourceName } from "../../common/types"
import { CommonFilterParamsV4 } from "../../filters/types"
import * as API from "../../api"
import { convertURLParamsToObject, getModifiersSummary, getTimezoneAcronym, minutesToHours, TKHours } from "../../common/ts-utils"
import { isNumber } from "../../common/validators"

export const dayIndices = [0, 1, 2, 3, 4, 5, 6]

/**
 * This is just here so it can be easily mocked in tests without
 * messing with jest.useFakeTimers() or mocking the global Date object.
 */
export const getGeneratedAtDate = (): Date => new Date()

/**
 * Fetch all pages of data from the v4 Internal API for the given query params.
 * @param resourceName used to lookup the related API v4 URL
 * @param params query params, already formatted for the API
 * @returns a flat list of items from the API
 */
export const loadTKEntriesV4 = async <T>(resourceName: tResourceName, params: CommonFilterParamsV4): Promise<T[]> => {
    const response = await API.getV4Resources(resourceName, params)
    const items: T[] = response.results
    let next = response.next
    while (next) {
        const response = await API.getV4Resources(resourceName, convertURLParamsToObject(next))
        items.push(...response.results)
        next = response.next
    }

    return items
}

/**
 * Utility function for Weekly TK time display.
 * Converts minutes to hours, but returns null in the case of a 0 value
 * (because nothing should display in the cell in this case).
 * @param minutes
 */
export const weeklyTKMinutesToHours = (minutes: number): string | null => {
    const roundedHours = minutesToHours(minutes)
    if (parseFloat(roundedHours) === 0) {
        return null
    }
    return roundedHours
}

// Finds the week "bucket" that the given date falls in, offset from the first week.
const getOffsetFromStartWeek = (date: Date, startDate: Date) => {
    return differenceInWeeks(date, startDate)
}

// Finds the day index of the given date in relation to the chosen start-of-week day.
const getAdjustedDayOfWeek = (date: Date, startOfWeek: number) => {
    const day = getDay(date)
    if (day < startOfWeek) {
        return day + 7 - startOfWeek
    }
    return day - startOfWeek
}

type CCMinutes = {
    [day: number]: {
        st: number
        ot: number
        dt: number
    }
    total: {
        st: number
        ot: number
        dt: number
    }
}

/**
 * Sum the hours for each day of the week, optionally excluding modifiers.
 * @param wcsByDay
 * @param options.excludeModifiers - if true, work components with modifiers will not be included in the total
 * @returns a record of the hours for each day of the week, and a total for all days
 */
export const sumDailyWCs = (
    wcsByDay: WeeklyTKEmployeeWCsByDay,
    options: { excludeModifiers?: boolean } = {
        excludeModifiers: false,
    }
): CCMinutes => {
    const ccMinutes = dayIndices.reduce<CCMinutes>(
        (acc, d) => {
            const time = {
                st: 0,
                ot: 0,
                dt: 0,
            }

            wcsByDay[d]?.forEach(({ adjusted_minutes_st, adjusted_minutes_ot, adjusted_minutes_dt, modifier_active }) => {
                const hasModifierToDisplay = modifier_active && Object.values(modifier_active).some(value => !isNumber(Number(value)))
                if (hasModifierToDisplay && options?.excludeModifiers) return
                time.st += adjusted_minutes_st
                time.ot += adjusted_minutes_ot
                time.dt += adjusted_minutes_dt
            })

            acc[d] = time
            acc.total.st += time.st
            acc.total.ot += time.ot
            acc.total.dt += time.dt
            return acc
        },
        { total: { st: 0, ot: 0, dt: 0 } } as CCMinutes
    )
    return ccMinutes
}

export type ModifierMinues = {
    [modifierString: string]: { [dayIndex: number]: TKHours; total: { st: number; ot: number; dt: number } }
}

/**
 * Sum the hours for each day of the week, for each unique modifier/cost code combination.
 * Note modifier keys include the cost code with a delimiter CC_MODIFIER_DELIMITER
 * @param wcsByDay
 * @param costCodes
 * @returns a record of the hours for each day of the week, and a total for all days, for each modifier
 */
export const sumDailyModifiers = (wcsByDay: WeeklyTKEmployeeWCsByDay): ModifierMinues => {
    const modifiersByDay: ModifierMinues = {}
    dayIndices.forEach(d => {
        // the true means to include the cost code id in the modifier key
        const modifiers = getModifiersSummary([{ work_components: wcsByDay[d] ?? [] }], true)
        Object.keys(modifiers).forEach(key => {
            if (!modifiersByDay[key])
                modifiersByDay[key] = {
                    [d]: {
                        ...modifiers[key],
                    },
                    total: { st: 0, ot: 0, dt: 0 },
                }
            if (!modifiersByDay[key][d]) modifiersByDay[key][d] = { ...modifiers[key] }

            modifiersByDay[key].total.st += modifiers[key]?.st ?? 0
            modifiersByDay[key].total.ot += modifiers[key]?.ot ?? 0
            modifiersByDay[key].total.dt += modifiers[key]?.dt ?? 0
        })
    })
    return modifiersByDay
}

/**
 * NOTE on RMBX timezones and date & time display (2025-03-13)
 * The 'date' field on Workshift, EWS, WC, Absence, StartStop, etc. models is relative to the timezone
 * of the client that first created the data.
 * e.g. a Foreman in Hawaii (UTC-10) creates a workshift on Fri 2025-03-14, with a start time of 8AM,
 * but the 'date' field doesn't include any TZ info -- it's just YYYY-MM-DD.
 *
 * Following the example above, with the Foreman in Hawaii, imagine the company HQ is located in Japan (UTC+9).
 * That's a +19 hour difference between the jobsite and the office.
 * Relative to the Office Staff in Japan, the workshift was created on Sat 2025-03-15; however,
 * when they open up Weekly View they'll see the time show up under the column for Fri 2025-03-14.
 *
 * This makes some sense. The Office Staff sees the workshift entries relative to the locale they were created in.
 * (Though it might make more sense to be relative to the project timezone).
 *
 * Where the system gets strange is with the data that is saved in UTC format,
 * for example Shift & Breaks start/stop times.
 *
 * Say the Hawaii Foreman created a timecard with a Shift start time of 8AM and a Meal start time of 12PM.
 * Those times get converted and saved in UTC, but the context of the original timezone is lost.
 *
 * Now, the Office Staff in Japan sees the Meal start at 7AM (12PM HST +19h to JST).
 * This is technically correct since that is exactly the time that the Meal started: 7AM JST on Sat 2025-03-15.
 * But, it's still going to show up under Fri 2025-03-14 in Weekly View.
 *
 * This is a mixture of two concepts with TK data relative to:
 * 1) the locale it was created in, and
 * 2) the locale it is viewed in
 *
 * I imagine for Office Staff, it would be easiest to review all the data
 * in a third concept: relative to the timezone the work was performed in.
 * But the next best would be to have all TK data relative to just one locale, either 1) or 2) above, not both.
 *
 * For now, Printable Weekly TK will align with Weekly View with the mixture
 * of created-in locale for YYYY-MM-DD dates and viewed-in locale for UTC timestamps.
 */
export const formatWeeklyTKDate = (date: Date): string => format(date, DATE_MM_DD_YYYY)
export const formatWeeklyTKTime = (date: Date): string => format(date, TIME_FORMAT)
export const formatWeeklyTKGeneratedAt = (date: Date): string => `${formatWeeklyTKDate(date)} ${formatWeeklyTKTime(date)} ${getTimezoneAcronym(undefined, date).trim()}`

/**
 * Counts the calendar days a shift's end falls after its start,
 * e.g. an overnight shift starting at 10:00 pm and ending at 6:00 am the next day is 1 day later.
 * Both timestamps are compared in the viewer's locale, consistent with how formatWeeklyTKTime displays them.
 *
 * @param startTime the shift's start timestamp (ISO string)
 * @param stopTime the shift's stop timestamp (ISO string)
 * @returns the number of calendar days the stop time is after the start time (0 if it ends the same day)
 */
export const getWeeklyTKShiftEndDaysLater = (startTime: string, stopTime: string): number => Math.max(differenceInCalendarDays(new Date(stopTime), new Date(startTime)), 0)

/**
 * Parse a YYYY-MM-DD to local time.
 * Just using new Date("<YYYY-MM-DD>") will parse the date string as a UTC date, which we don't want.
 * e.g. from a client in the America/Los_Angeles timezone
 * We don't want:
 * const date = new Date("2025-03-15")
 * formatWeeklyTKDate(date) => 03/14/2025
 *
 * We do want:
 * const date = parseDateStrToLocalTime("2025-03-15")
 * formatWeeklyTKDate(date) => 03/15/2025
 */
export const parseDateStrToLocalTime = (dateStr: string): Date => parse(dateStr, STORED_DATE_ONLY_FORMAT, new Date())

/**
 * Some data needs to be referenced in multiple parts of the table, like Approver and Signature statuses,
 * which will have a cell value as well as inform what footnotes are shown at the bottom of the table.
 * Instead of having individual components recalculate that data, it can be computed once upfront here.
 */
const annotateWeekEmployeeData = (blob: WeeklyTKData["dataByWeekOffset"], approvalStatuses: string[]) => {
    // Loop over data for each week.
    Object.values(blob).forEach(weekDataByEmp =>
        // Loop over data for each employee within the week.
        Object.values(weekDataByEmp).map(empWeekData => {
            // Annotate some data per day of the week that will be referenced in multiple parts of the table.
            dayIndices.map(d => {
                const { absences, startStops, wcs, statuses } = empWeekData!.byDay[d]
                // Per day of the week, build up a mapping of approval status to approver IDs for that status.
                // There may be multiple approvers for the same status if multiple timecards are in the mix.
                const approversByStatus: WeeklyTKApproversByStatus = approvalStatuses.reduce((acc, status) => {
                    acc[status] = { state: undefined, approvers: new Set(), someUnapproved: false }
                    return acc
                }, {} as WeeklyTKEmployeeDataByDay[number]["statuses"]["approversByStatus"])
                for (const tkes of [wcs, absences, startStops]) {
                    for (const tke of tkes) {
                        const approvers_by_status = tke.approvers_by_status || {}
                        for (const status of approvalStatuses) {
                            if (approvers_by_status[status]) {
                                approversByStatus[status].approvers.add(approvers_by_status[status])
                            } else {
                                // Found a TK Entry without an approver for the given status,
                                // so at least some data has not been approved.
                                approversByStatus[status].someUnapproved = true
                            }
                        }
                    }
                }
                for (const status of approvalStatuses) {
                    const statusInfo = approversByStatus[status]
                    const { approvers, someUnapproved } = statusInfo
                    const approverCount = approvers.size

                    statusInfo.state = someUnapproved
                        ? approverCount > 1 // Some data is approved, and multiple approvers are present.
                            ? "partial-multiple"
                            : approverCount === 1 // Some data is approved, and only by one approver.
                            ? "partial"
                            : null // There is data, but none of it has been approved yet (0 approvers).
                        : approverCount > 1
                        ? "multiple" // All data approved, multiple approvers.
                        : approverCount === 1
                        ? [...approvers][0] // All data approved, one approver.
                        : null // No data (technically no unapproved time, but 0 approvers).
                }
                statuses.approversByStatus = approversByStatus

                const signatureCount = wcs.filter(wc => wc.signature).length
                statuses.signatures =
                    wcs.length === 0
                        ? null // No WCs
                        : signatureCount === wcs.length // All WCs signed for
                        ? true
                        : signatureCount > 0 // Some WCs signed for
                        ? "partial"
                        : null
            })
        })
    )
    return blob
}

const getWeekEmployeeData = (blob: WeeklyTKData["dataByWeekOffset"], startDate: Date, weekOffset: number, employeeId: number) => {
    if (!blob[weekOffset]) {
        blob[weekOffset] = {}
    }
    if (!blob[weekOffset][employeeId]) {
        blob[weekOffset][employeeId] = {
            employeeId: employeeId,
            startOfWeek: addWeeks(startDate, weekOffset),
            byDay: dayIndices.reduce<WeeklyTKEmployeeDataByDay>((acc, d) => {
                acc[d] = {
                    absences: [],
                    ewsIds: new Set(),
                    ownerIds: new Set(),
                    startStops: [],
                    wcs: [],
                    shiftExtras: [],
                    statuses: {
                        approversByStatus: {},
                        signatures: undefined,
                    },
                }
                return acc
            }, {} as WeeklyTKEmployeeDataByDay),
            byProject: {},
            comments: [],
        }
    }
    const weekData = blob[weekOffset][employeeId]!
    return weekData
}

const addReferencedApproverIds = (tke: TimekeepingEntry | Absence | EmployeeWorkShiftStartStopTime, ids: Set<number>) => {
    if (tke.approvers_by_status) {
        Object.keys(tke.approvers_by_status).forEach(status => {
            if (tke.approvers_by_status![status]) {
                ids.add(tke.approvers_by_status![status])
            }
        })
    }
}

export const organizeWeeklyTKData = (
    startDate: Date,
    { wcs, absences, startStops, shiftExtras }: Pick<WeeklyTKEmployeeDataByDay[number], "absences" | "startStops" | "wcs" | "shiftExtras">,
    approvalStatuses: string[]
): [WeeklyTKData["dataByWeekOffset"], WeeklyTKReferencedIds] => {
    // Assuming the date range contains a number of complete weeks,
    // this determines what day of the week represents the start of the week.
    // 0 -> Sunday, 6 -> Saturday
    const startOfWeek = getDay(startDate)

    const referencedIds: WeeklyTKReferencedIds = {
        ccIds: new Set(),
        employeeIds: new Set(),
        ewsIds: new Set(),
        projectIds: new Set(),
        ewsSchemaIds: new Set(),
    }
    const blob: WeeklyTKData["dataByWeekOffset"] = {}

    for (const wc of wcs) {
        referencedIds.ccIds.add(wc.cost_code as number)
        referencedIds.employeeIds.add(wc.employee)
        referencedIds.ewsIds.add(wc.employee_work_shift_id)
        referencedIds.projectIds.add(wc.project)
        referencedIds.employeeIds.add(wc.foreman)
        addReferencedApproverIds(wc, referencedIds.employeeIds)

        const date = parseDateStrToLocalTime(wc.date)
        const day = getAdjustedDayOfWeek(date, startOfWeek)
        const weekOffset = getOffsetFromStartWeek(date, startDate)
        const weekData = getWeekEmployeeData(blob, startDate, weekOffset, wc.employee)

        const byDay = weekData.byDay
        byDay[day].wcs.push(wc)
        byDay[day].ewsIds.add(wc.employee_work_shift_id)
        byDay[day].ownerIds.add(wc.foreman)

        if (wc.comment) {
            weekData.comments.push({
                ccId: wc.cost_code as number,
                comment: wc.comment,
                date: wc.date,
                projectId: wc.project,
                wcId: wc.id,
            })
        }

        const byProject = weekData.byProject
        if (!byProject[wc.project]) {
            byProject[wc.project] = {}
        }
        if (!byProject[wc.project][wc.cost_code as number]) {
            byProject[wc.project][wc.cost_code as number] = {}
        }
        if (!byProject[wc.project][wc.cost_code as number][day]) {
            byProject[wc.project][wc.cost_code as number][day] = []
        }
        byProject[wc.project][wc.cost_code as number][day]!.push(wc)
    }

    for (const shiftExtra of shiftExtras) {
        referencedIds.employeeIds.add(shiftExtra.employee)
        referencedIds.ewsIds.add(shiftExtra.ews_id)
        referencedIds.ewsSchemaIds.add(shiftExtra.schema)

        const date = parseDateStrToLocalTime(shiftExtra.date)
        const day = getAdjustedDayOfWeek(date, startOfWeek)
        const weekOffset = getOffsetFromStartWeek(date, startDate)
        const weekData = getWeekEmployeeData(blob, startDate, weekOffset, shiftExtra.employee)

        const byDay = weekData.byDay
        byDay[day].shiftExtras.push(shiftExtra)
        byDay[day].ewsIds.add(shiftExtra.ews_id)
    }

    for (const absence of absences) {
        referencedIds.employeeIds.add(absence.employee)
        referencedIds.ewsIds.add(absence.employee_work_shift_id)
        // Not adding referenced project IDs, because projects of absences aren't displayed in the UI.

        referencedIds.employeeIds.add(absence.foreman)
        addReferencedApproverIds(absence, referencedIds.employeeIds)

        const date = parseDateStrToLocalTime(absence.date)
        const day = getAdjustedDayOfWeek(date, startOfWeek)
        const weekOffset = getOffsetFromStartWeek(date, startDate)
        const weekData = getWeekEmployeeData(blob, startDate, weekOffset, absence.employee)

        const byDay = weekData.byDay
        byDay[day].absences.push(absence)
        byDay[day].ewsIds.add(absence.employee_work_shift_id)
        byDay[day].ownerIds.add(absence.foreman)
    }

    for (const startStop of startStops) {
        referencedIds.employeeIds.add(startStop.employee)
        referencedIds.ewsIds.add(startStop.employee_work_shift_id)

        referencedIds.employeeIds.add(startStop.foreman)
        addReferencedApproverIds(startStop, referencedIds.employeeIds)

        const date = parseDateStrToLocalTime(startStop.date)
        const day = getAdjustedDayOfWeek(date, startOfWeek)
        const weekOffset = getOffsetFromStartWeek(date, startDate)
        const weekData = getWeekEmployeeData(blob, startDate, weekOffset, startStop.employee)

        const byDay = weekData.byDay
        byDay[day].startStops.push(startStop)
        byDay[day].ewsIds.add(startStop.employee_work_shift_id)
        byDay[day].ownerIds.add(startStop.foreman)
    }

    annotateWeekEmployeeData(blob, approvalStatuses)

    return [blob, referencedIds]
}

export const getEmployeeNamesForCell = (ids: number[], employeeMap: Record<number, iEmployee>): string[] => {
    return [...ids]
        .map(id => {
            const { first_name, last_name } = employeeMap[id]
            return [last_name, first_name].filter(Boolean).join(", ")
        })
        .sort((a, b) => a.localeCompare(b))
        .map((name, index) => (index < ids.length - 1 ? `${name};` : name))
}
